import { Injectable, OnDestroy, signal } from '@angular/core';
import { GridApi, ColumnState, AgPublicEventType } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { debounceTime, Observable, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';

import { ApiUsersService } from '@shure/cloud/shared/users/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

export interface GridConfig {
	columnState: ColumnState[];
	rowGroupColumns: string[];
}

export interface GridConfigChangeEvent {
	componentId: string;
	configuration: GridConfig;
}

@Injectable({
	providedIn: 'root'
})
export class UserAgGridPreferencesService implements OnDestroy {
	public gridPreferencesSaved = signal<GridConfigChangeEvent | undefined>(undefined);
	private configChange$ = new Subject<GridConfigChangeEvent>();

	// Debounce time for saving configurations
	private saveTimeout = 2000;
	private destroy$ = new Subject<void>();
	private isLoadingConfig = false;
	private initialGridConfig!: GridConfig;

	// The ag-grid changes/events to monitor and to trigger auto saving.
	private autoSaveEvents: AgPublicEventType[] = [
		'columnMoved',
		'columnVisible',
		'columnPinned',
		'sortChanged',
		'columnRowGroupChanged'
	];

	constructor(
		private readonly userService: ApiUsersService,
		private readonly logger: ILogger
	) {
		// Automatically handle saving when a configuration change is triggered
		this.configChange$
			.pipe(debounceTime(this.saveTimeout), takeUntil(this.destroy$))
			.subscribe((gridConfigSaveEvent) => {
				this.saveUserSettings(gridConfigSaveEvent);
			});
	}

	/**
	 * Sets up auto-save functionality for grid changes.
	 * @param gridApi The AG Grid API instance.
	 * @param saveCallback Callback function to trigger on auto-save events.
	 */
	public setupAutoSave(gridApi: GridApi, saveCallback: () => void): void {
		this.autoSaveEvents.forEach((event) => gridApi?.addEventListener(event, saveCallback));
	}

	/**
	 * Removes the event listeners for the grid so the callback function
	 * will not be called. When removing listeners, it is important to use
	 * the same function reference that was used in the setup.
	 * @param gridApi The AG Grid API instance.
	 * @param saveCallback Callback associated with a prior call to setupAutoSave
	 */
	public removeAutoSave(gridApi: GridApi, saveCallback: () => void): void {
		this.autoSaveEvents.forEach((event) => gridApi?.removeEventListener(event, saveCallback));
	}

	/**
	 * Loads the grid configuration for a specific component.
	 * @param gridApi The AG Grid API instance.
	 * @param componentId Unique key for identifying the grid configuration.
	 * @param gridConfigMigrator a function to call to merge a new Config into an existing one.
	 * 		By default ag-grid will append new columns to the end of the table, but that may
	 * 		not be the preferred location for a new column.
	 * @returns an observable that emits a true value when the loading is complete or an error occurred.
	 */
	public loadGridConfig(
		gridApi: GridApi,
		componentId: string,
		gridConfigMigrator?: (savedGridConfig: GridConfig) => GridConfig
	): Observable<boolean> {
		const retVal = new Subject<boolean>();

		this.isLoadingConfig = true;
		this.userService
			.getUserApplicationPreferences$Response({ componentIds: this.getComponentId(componentId) })
			.pipe(
				map((response) => {
					const responseData = response.body;
					if (Array.isArray(responseData.body) && responseData.body.length > 0) {
						let configuration = responseData.body[0].configuration as GridConfig;
						// If a migrator is provided, invoke it and use the returned gridConfig
						if (gridConfigMigrator) {
							configuration = gridConfigMigrator(configuration);
						}
						this.initialGridConfig = configuration;
						if (configuration?.columnState) {
							gridApi.applyColumnState({ state: configuration.columnState, applyOrder: true });
							gridApi.setRowGroupColumns(configuration.rowGroupColumns);
						}
						gridApi.refreshHeader(); //Forces a re-render of the grid's header after the content changed
					}
				}),
				catchError((error) => {
					retVal.next(true);
					this.isLoadingConfig = false; // Re-enable auto-save after loading
					this.logger.error('Error loading grid configuration:', error);
					const errorMessage = error.error ? JSON.parse(error.error) : 'Unknown error';
					throw errorMessage;
				})
			)
			.subscribe({
				complete: () => {
					retVal.next(true);
					this.isLoadingConfig = false; // Re-enable auto-save after loading
				},
				// catchError already logs and settles retVal; this handler only prevents an unhandled subscription error.
				error: () => undefined
			});
		return retVal.asObservable();
	}

	/**
	 * Saves the current grid configuration.
	 * @param gridApi The AG Grid API instance.
	 * @param componentId Unique key for identifying the grid configuration.
	 */
	public saveGridConfig(gridApi: GridApi, componentId: string): void {
		if (this.isLoadingConfig) {
			return; // Skip saving during config loading
		}
		const columnState = gridApi.getColumnState();
		const rowGroupColumns = gridApi.getRowGroupColumns().map((col) => col.getColId());

		const configuration: GridConfig = {
			columnState,
			rowGroupColumns
		};
		// Saving of the grid configuration.
		if (this.isGridConfigEqual(configuration, this.initialGridConfig)) {
			return; // No changes detected, skip saving
		}
		this.configChange$.next({ componentId: this.getComponentId(componentId), configuration });
	}

	/**
	 * Cleanup resources on service destruction.
	 */
	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Saves the grid configuration for a component.
	 * @param body The payload containing the grid configuration.
	 */
	private saveUserSettings(body: GridConfigChangeEvent): void {
		this.userService
			.updateUserApplicationPreferences$Response({ body })
			.pipe(
				takeUntil(this.destroy$),
				tap(() => {
					this.gridPreferencesSaved.set(body);
				})
			)
			.subscribe({
				next: (response) => {
					const updatePreferencesResponse = response?.body?.body?.message as string;
					this.logger.information('response', updatePreferencesResponse);
				},
				error: (error) => {
					this.logger.error('Error saving grid configuration:', error);
				}
			});
	}

	private getComponentId(componentId: string): string {
		return `shurecloud-${componentId}_table-config`;
	}

	private isGridConfigEqual(config1: GridConfig | null, config2: GridConfig | null): boolean {
		if (!config1 || !config2) {
			return false; // If either config is null, they are not equal
		}

		return isEqual(config1, config2);
	}
}
